import { z } from "zod";
import {
  COACH_WORK_QUEUE_LANES,
  DOCTOR_WORK_QUEUE_LANES,
  workQueueLaneSchema,
  type WorkQueueLane,
} from "./work-queue";

// TES-746 — the coach's patient memory: the 13 areas the coach workspace's
// "Patientbild" is built from, plus the personal follow-ups
// ("Följ upp senare") the coach schedules off them.
//
// The vocabulary comes from the MVP implementation contract (Drive,
// 2026-09-16), which defines `CoachMemoryItem` with SCREAMING_CASE enum
// values. This repo spells every persisted enum in lower snake_case
// (`renewal_pending`, `acknowledged`, `pending_review`), so the values below
// are the snake_case spelling of that contract, 1:1 and in the same order:
//
//   GOAL→goal · CHALLENGE→challenge · MOTIVATION→motivation
//   PREFERENCE→preference · HABIT_ROUTINE→habit_routine
//   DESIRED_SUPPORT→desired_support · AGREEMENT→agreement · WORKED→worked
//   DID_NOT_WORK→did_not_work · RECURRING_PATTERN→recurring_pattern
//   FOLLOW_UP_QUESTION→follow_up_question
//   CHANGE_SINCE_CONTACT→change_since_contact · OPEN_FOLLOW_UP→open_follow_up
//
// Two invariants ride on this file and must not be softened:
//
//  1. A memory item NEVER decides `Patient.journeyState`. The implementation
//     contract is explicit that AI summaries and preliminary indicators never
//     move a patient's state; `JourneyStateService.advance()` stays the single
//     writer. Nothing here is an input to it.
//  2. `body` is field-encrypted (ENCRYPTED_FIELDS) and therefore can never be
//     queried by value. Every filter the surfaces need must ride on
//     `category`, `factType`, `lifecycle` or the source coordinates — never on
//     the text. Adding a "search the memory" feature means adding a blind
//     index, not a `contains`.

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

// The areas: the contract's 13 plus "Relevant hänsyn" (TES-1019) and
// "Tidigare försök" (TES-1103). Order is the contract's order — the
// Patientbild renders them in it, so it is presentation-significant, not
// just a list.
export const COACH_MEMORY_CATEGORIES = [
  "goal",
  "challenge",
  "motivation",
  "preference",
  "habit_routine",
  "desired_support",
  "agreement",
  // TES-1103 (Ö-320, Emelie 2026-10-04: "implement per the current Drive") —
  // "Tidigare försök". A-01's F-01 addendum lists "tidigare försök" as its
  // own category, and its relationship-memory section reads "vad patienten
  // tidigare har provat; vad som har fungerat eller inte fungerat", so it
  // sits right before "Fungerade" / "Fungerade inte". Persistent knowledge
  // (defaultMemoryClass): an earlier attempt is background, not current news.
  "previous_attempt",
  "worked",
  "did_not_work",
  "recurring_pattern",
  "follow_up_question",
  "change_since_contact",
  "open_follow_up",
  // TES-1019 (Emelie, point 11) — "Relevant hänsyn": what the coach needs to
  // know to meet the patient well, minimised from sensitive background, e.g.
  // "Patienten kan behöva ett varsamt, traumamedvetet bemötande." rather than
  // an unconfirmed diagnosis label. Appended, so the existing 13 keep their
  // order.
  "relevant_consideration",
] as const;
export const coachMemoryCategorySchema = z.enum(COACH_MEMORY_CATEGORIES);
export type CoachMemoryCategory = z.infer<typeof coachMemoryCategorySchema>;

// What KIND of claim the row is — orthogonal to which area it belongs to.
// `ai_observation` is the one the UI must mark as a hypothesis: the package is
// explicit that AI observations are never presented as fact. `agreement`
// appears on both axes on purpose: an agreement can be filed under any area
// (an agreement about habits, an agreement about support), and the fact-type
// is what makes it binding rather than descriptive.
//
// TES-1019 (Emelie, point 10) — `doctor_handoff`: something a named doctor
// handed over to the coach. Her rule is that it
// "får inte felmärkas som coachanteckning, patientens ord eller överenskommelse",
// so it was a kind of its own, written only by the handoff route and never
// offered in the free "Lägg till" form.
//
// TES-1367 (Emelie, Ö-462, 2026-10-15) — that route is gone:
// "Läkaröverlämningar, journaltext och annan medicinsk information får inte sparas som Patientminne."
// Nothing writes `doctor_handoff` any more; the value stays so a row written
// before can still be read (QA and prod held none on 2026-10-15).
export const COACH_MEMORY_FACT_TYPES = [
  "patient_statement",
  "agreement",
  "ai_observation",
  "doctor_handoff",
] as const;
export const coachMemoryFactTypeSchema = z.enum(COACH_MEMORY_FACT_TYPES);
export type CoachMemoryFactType = z.infer<typeof coachMemoryFactTypeSchema>;

// Lifecycle. Nothing is ever hard-deleted or overwritten:
//   active      — the current truth, the only state the Patientbild shows
//   superseded  — replaced by a newer row that points back via supersedesId
//   completed   — a follow-up question or open follow-up that got its answer
//   inactive    — switched off without saying it was wrong. Since TES-1019
//                 (Emelie, point 12: removal is normally a traceable
//                 inactivation) this is also what the remove button sets;
//                 the row and its text stay readable under the history.
//   deleted     — set only by the source-deletion cascade (TES-753): the
//                 object it came from, e.g. a chat message, was deleted, so
//                 its text is not shown again, not even in the history.
//                 Also the result of erasing an entry on the patient's
//                 deletion request (TES-1237): the text is scrubbed.
//   restricted  — TES-1367: restricted on the patient's request ("begränsning",
//                 GDPR Art. 18). The row and its text are kept (her Art. 15
//                 export still discloses it) but no coach view or AI draft
//                 uses it; the coach's history lists it without its text.
export const COACH_MEMORY_LIFECYCLES = [
  "active",
  "superseded",
  "completed",
  "inactive",
  "deleted",
  "restricted",
] as const;
export const coachMemoryLifecycleSchema = z.enum(COACH_MEMORY_LIFECYCLES);
export type CoachMemoryLifecycle = z.infer<typeof coachMemoryLifecycleSchema>;

// Where the claim came from. NOT decoration: `sourceType` + `sourceId` are the
// only way to find every memory item derived from one object, which is what
// the package's rule
// "borttagen information får inte ligga kvar som aktivt AI-minne"
// requires when a chat message is deleted. `coach_note` is the coach writing
// directly in the "Patientbild", where the source is the author.
export const COACH_MEMORY_SOURCE_TYPES = [
  "form_submission",
  "message",
  "patient_reflection",
  "meeting",
  "ai_assist_draft",
  "coach_note",
  // TES-1019 (Emelie, point 10) — a doctor's handoff to the coach; `sourceId`
  // is the Handoff's id. Only the handoff route writes it.
  "doctor_handoff",
] as const;
export const coachMemorySourceTypeSchema = z.enum(COACH_MEMORY_SOURCE_TYPES);
export type CoachMemorySourceType = z.infer<typeof coachMemorySourceTypeSchema>;

// TES-1192 (Emelie 2026-10-08, TES-1173 point 3) — the five classes
// Patientminnet distinguishes, orthogonal to the area (`category`):
// "Beständig kunskap, Aktuellt fokus, Tillfällig kontext, Överenskommelse,
// Uppföljning". The area says WHAT the row is about; the class says how long
// it holds and whether it belongs under "Aktuellt".
export const COACH_MEMORY_CLASSES = [
  "persistent",
  "current_focus",
  "temporary_context",
  "agreement",
  "follow_up",
] as const;
export const coachMemoryClassSchema = z.enum(COACH_MEMORY_CLASSES);
export type CoachMemoryClass = z.infer<typeof coachMemoryClassSchema>;

export const COACH_MEMORY_CLASS_LABELS: Record<CoachMemoryClass, string> = {
  persistent: "Beständig kunskap",
  current_focus: "Aktuellt fokus",
  temporary_context: "Tillfällig kontext",
  agreement: "Överenskommelse",
  follow_up: "Uppföljning",
};

// The classes shown under "Aktuellt", in this order. The persistent class is
// the background the coach reads by area, not current news.
export const CURRENT_MEMORY_CLASSES: readonly CoachMemoryClass[] = [
  "current_focus",
  "temporary_context",
  "agreement",
  "follow_up",
];

// The class a row gets when nobody chose one. The migration's backfill and
// every writer that predates the class axis (F-01 seeding, check-in
// observations) use this one mapping, so a row reads the same whichever path
// wrote it.
export function defaultMemoryClass(category: string): CoachMemoryClass {
  switch (category) {
    case "agreement":
      return "agreement";
    case "open_follow_up":
    case "follow_up_question":
      return "follow_up";
    case "change_since_contact":
      return "current_focus";
    default:
      return "persistent";
  }
}

// TES-1192 — where a row stands in time on `today` (YYYY-MM-DD in the
// tenant's zone). Only a temporary context has dates; every other class is
// `null` here and holds for as long as it is active.
//
//   upcoming  validFrom is after today: written down ahead of time
//   current   today is within [validFrom, validUntil], both days included
//   expired   validUntil is before today
//
// This is a READ rule. Nothing flips a lifecycle when the end date passes:
// the row stays `active` and simply stops being current, so it leaves
// "Aktuellt", shows under "Historik" with its dates, and is never handed to a
// draft as if it still held. Emelie:
// "Systemet får inte flera månader senare formulera sig som om patienten fortfarande är bortrest."
export type CoachMemoryTemporalState = "upcoming" | "current" | "expired";

export function memoryTemporalState(
  item: {
    memoryClass?: CoachMemoryClass;
    validFrom?: string | null;
    validUntil?: string | null;
  },
  today: string,
): CoachMemoryTemporalState | null {
  if (item.memoryClass !== "temporary_context") return null;
  if (item.validFrom && item.validFrom > today) return "upcoming";
  if (item.validUntil && item.validUntil < today) return "expired";
  return "current";
}

// The one predicate every "what holds now" reader uses: the Patientbild's
// areas, "Aktuellt", the coach summaries and the message drafts.
export function isCurrentMemoryItem(
  item: {
    lifecycle: string;
    memoryClass?: CoachMemoryClass;
    validFrom?: string | null;
    validUntil?: string | null;
  },
  today: string,
): boolean {
  if (item.lifecycle !== "active") return false;
  const state = memoryTemporalState(item, today);
  return state === null || state === "current";
}

// ---------------------------------------------------------------------------
// Labels (Swedish; the admin is Swedish-only and no English labels existed)
// ---------------------------------------------------------------------------

export const COACH_MEMORY_CATEGORY_LABELS: Record<CoachMemoryCategory, string> =
  {
    goal: "Mål",
    challenge: "Utmaning",
    motivation: "Motivation",
    preference: "Preferens",
    habit_routine: "Vana och rutin",
    desired_support: "Önskat stöd",
    agreement: "Överenskommelse",
    previous_attempt: "Tidigare försök",
    worked: "Fungerade",
    did_not_work: "Fungerade inte",
    recurring_pattern: "Återkommande mönster",
    follow_up_question: "Uppföljningsfråga",
    change_since_contact: "Förändring sedan senaste kontakten",
    open_follow_up: "Öppen uppföljning",
    relevant_consideration: "Relevant hänsyn",
  };

export const COACH_MEMORY_FACT_TYPE_LABELS: Record<
  CoachMemoryFactType,
  string
> = {
  patient_statement: "Patientens ord",
  agreement: "Överenskommelse",
  ai_observation: "AI-observation",
  doctor_handoff: "Läkaröverlämning",
};

// TES-1019 — the source ("Källa") as a person reads it. A form's source is
// "Patienten – <form>", and the F-01 answers read
// "Patienten – Första frågorna" (Emelie, point 9). `templateKey` is the
// submission's form when the source is a form submission.
export const F01_MEMORY_SOURCE_LABEL = "Patienten – Första frågorna";

const SOURCE_TYPE_LABELS: Record<CoachMemorySourceType, string> = {
  form_submission: "Patienten – formulär",
  message: "Chatt",
  patient_reflection: "Patienten – reflektion",
  meeting: "Vårdmöte",
  ai_assist_draft: "AI-utkast",
  coach_note: "Coachanteckning",
  // TES-1034 (Emelie's point 8) — the source reads "Läkare/vårdteam". The
  // enum value is unchanged; who conveyed it and when are shown beside it.
  doctor_handoff: "Läkare/vårdteam",
};

const FORM_SOURCE_LABELS: Record<string, string> = {
  f01: F01_MEMORY_SOURCE_LABEL,
  intake: "Patienten – Medicinsk hälsodeklaration",
  prescription_transfer: "Patienten – Läkemedel och behandlingsunderlag",
  weekly_checkin: "Patienten – Veckoincheckning",
  milestone: "Patienten – Månadsincheckning",
};

export function coachMemorySourceLabel(item: {
  sourceType: CoachMemorySourceType;
  sourceTemplateKey?: string | null;
}): string {
  if (item.sourceType === "form_submission" && item.sourceTemplateKey) {
    return (
      FORM_SOURCE_LABELS[item.sourceTemplateKey] ??
      SOURCE_TYPE_LABELS.form_submission
    );
  }
  return SOURCE_TYPE_LABELS[item.sourceType];
}

// ---------------------------------------------------------------------------
// Follow-up ("Följ upp senare")
// ---------------------------------------------------------------------------

// scheduled → due is the cron's job (dueAt passes). due → completed|cancelled
// is the coach. A `due` row is what the work queue projects as a lane item, so
// the follow-up never becomes a second, parallel queue.
export const COACH_FOLLOW_UP_STATUSES = [
  "scheduled",
  "due",
  "completed",
  "cancelled",
] as const;
export const coachFollowUpStatusSchema = z.enum(COACH_FOLLOW_UP_STATUSES);
export type CoachFollowUpStatus = z.infer<typeof coachFollowUpStatusSchema>;

export const COACH_FOLLOW_UP_TRANSITIONS: Record<
  CoachFollowUpStatus,
  CoachFollowUpStatus[]
> = {
  scheduled: ["due", "completed", "cancelled"],
  due: ["completed", "cancelled"],
  completed: [],
  cancelled: [],
};

export function canTransitionFollowUp(
  from: CoachFollowUpStatus,
  to: CoachFollowUpStatus,
): boolean {
  return COACH_FOLLOW_UP_TRANSITIONS[from].includes(to);
}

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

export const coachMemoryItemSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  category: coachMemoryCategorySchema,
  factType: coachMemoryFactTypeSchema,
  lifecycle: coachMemoryLifecycleSchema,
  body: z.string(),
  sourceType: coachMemorySourceTypeSchema,
  sourceId: z.string(),
  // When the underlying thing was said/observed — not when the row was
  // written. The Patientbild sorts on this; an item mined out of a two-week-
  // old check-in belongs at that date, not at today.
  observedAt: z.string(),
  // The row this one replaced, if any. Walking the chain backwards is the
  // history of one fact.
  supersedesId: z.string().nullable(),
  // Null when the model authored it — that is precisely what makes it a
  // hypothesis rather than something a human wrote down. "Registrerat av";
  // null reads "Skapad automatiskt".
  createdByEmployeeId: z.string().nullable(),
  // TES-1019 (Emelie, point 9) — the provenance a person reads. Optional so
  // an older server's answer still parses.
  //   conveyedByEmployeeId — "Förmedlat av": the named doctor behind a
  //                          handoff saved to memory; null otherwise.
  //   *DisplayName         — the employees' names, resolved by the server.
  //   sourceTemplateKey    — the form, when the source is a submission, so
  //                          F-01 reads "Patienten – Första frågorna".
  conveyedByEmployeeId: z.string().nullable().optional(),
  conveyedByDisplayName: z.string().nullable().optional(),
  createdByDisplayName: z.string().nullable().optional(),
  sourceTemplateKey: z.string().nullable().optional(),
  // An ai_observation that a coach has confirmed stops being a hypothesis.
  // Never auto-set: the package requires the coach to confirm explicitly.
  confirmedByEmployeeId: z.string().nullable(),
  confirmedAt: z.string().nullable(),
  createdAt: z.string(),
  // TES-1192 — the class axis and a temporary context's own fields. All
  // optional so an older server's answer still parses (it reads as
  // persistent with no dates).
  //   validFrom/validUntil — YYYY-MM-DD, the tenant's calendar days, both
  //                          included. Only a temporary context has them.
  //   impact               — "påverkan på behandlingsvardagen"
  //   agreement            — an agreement made about the period, if any
  //   followUpId           — the CoachFollowUp created with it, if any
  //   temporal             — memoryTemporalState on the server's today
  memoryClass: coachMemoryClassSchema.optional(),
  validFrom: z.string().nullable().optional(),
  validUntil: z.string().nullable().optional(),
  impact: z.string().nullable().optional(),
  agreement: z.string().nullable().optional(),
  followUpId: z.string().nullable().optional(),
  temporal: z.enum(["upcoming", "current", "expired"]).nullable().optional(),
});
export type CoachMemoryItem = z.infer<typeof coachMemoryItemSchema>;

// The Patientbild's read shape: the 13 areas, each with its active rows. Areas
// with nothing in them are still present so the surface can render an honest
// empty state per area rather than silently hiding a heading.
export const coachMemoryAreaSchema = z.object({
  category: coachMemoryCategorySchema,
  items: z.array(coachMemoryItemSchema),
});
export type CoachMemoryArea = z.infer<typeof coachMemoryAreaSchema>;

export const coachMemorySchema = z.object({
  patientId: z.string(),
  areas: z.array(coachMemoryAreaSchema),
  // TES-1019 (Emelie, point 12) — every row that is no longer active:
  // superseded versions (walk `supersedesId` from an active row), inactivated
  // and completed rows. A `deleted` row (its source was deleted, TES-753)
  // comes with an empty `body`. Optional so an older server's answer parses.
  history: z.array(coachMemoryItemSchema).optional(),
  // TES-1192 — the current rows grouped by class, every class present
  // (CURRENT_MEMORY_CLASSES order, then persistent) so the surface can render
  // an honest empty group. `areas` above holds the same current rows by area.
  // Neither ever carries an expired temporary context: that one is in
  // `history`, still `active`, with its dates. `upcoming` is a temporary
  // context whose start date has not come yet. `today` is the calendar day
  // (tenant zone) the server judged "current" against. All optional so an
  // older server's answer parses.
  classes: z
    .array(
      z.object({
        memoryClass: coachMemoryClassSchema,
        items: z.array(coachMemoryItemSchema),
      }),
    )
    .optional(),
  upcoming: z.array(coachMemoryItemSchema).optional(),
  today: z.string().optional(),
});
export type CoachMemory = z.infer<typeof coachMemorySchema>;

// The provenance only the doctor's handoff route may write. A free-standing
// item claiming to be a doctor's handoff would put a doctor's name on words
// nobody checked he conveyed.
const HANDOFF_ONLY = "doctor_handoff" as const;

// Writing a new item. `sourceType`/`sourceId` are required — there is no such
// thing as a memory item with no provenance; a coach writing freehand files it
// as `coach_note` against their own employee id.
const isoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
// Empty text from a cleared form field reads as "not given".
const optionalTextSchema = z
  .string()
  .trim()
  .max(2000)
  .optional()
  .transform((v) => (v ? v : undefined));

type TemporalFields = {
  memoryClass?: CoachMemoryClass;
  validFrom?: string | null;
  validUntil?: string | null;
  impact?: string | null;
  agreement?: string | null;
  followUpOn?: string | null;
};

// TES-1192 — a temporary context needs an end date (that is what makes it
// temporary) and a start no later than its end. The other classes carry no
// dates, impact, agreement or follow-up date. Used by the create schema and,
// on the combined result of a revision, by the service.
export function temporalFieldIssues(
  v: TemporalFields,
): { path: keyof TemporalFields; message: string }[] {
  if (v.memoryClass === "temporary_context") {
    const issues: { path: keyof TemporalFields; message: string }[] = [];
    if (!v.validUntil) {
      issues.push({
        path: "validUntil",
        message: "temporary_context_needs_end_date",
      });
    }
    if (v.validFrom && v.validUntil && v.validFrom > v.validUntil) {
      issues.push({ path: "validUntil", message: "end_before_start" });
    }
    return issues;
  }
  const keys = [
    "validFrom",
    "validUntil",
    "impact",
    "agreement",
    "followUpOn",
  ] as const;
  return keys
    .filter((k) => v[k] !== undefined && v[k] !== null)
    .map((k) => ({ path: k, message: "only_for_temporary_context" }));
}

export const createCoachMemoryItemSchema = z
  .object({
    category: coachMemoryCategorySchema,
    factType: coachMemoryFactTypeSchema,
    body: z.string().trim().min(1).max(4000),
    sourceType: coachMemorySourceTypeSchema,
    sourceId: z.string().min(1),
    observedAt: z.string().datetime().optional(),
    // TES-1192 — omitted = defaultMemoryClass(category).
    memoryClass: coachMemoryClassSchema.optional(),
    // Temporary context only; validFrom omitted = today.
    validFrom: isoDateSchema.optional(),
    validUntil: isoDateSchema.optional(),
    impact: optionalTextSchema,
    agreement: optionalTextSchema,
    // "om och när coachen ska följa upp": a date schedules a CoachFollowUp,
    // which lands in the coach's follow-up lane that morning.
    followUpOn: isoDateSchema.optional(),
  })
  .superRefine((v, ctx) => {
    for (const issue of temporalFieldIssues(v)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [issue.path],
        message: issue.message,
      });
    }
    for (const key of ["factType", "sourceType"] as const) {
      if (v[key] === HANDOFF_ONLY) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [key],
          message: "doctor_handoff_via_handoff_route",
        });
      }
    }
  });
export type CreateCoachMemoryItemRequest = z.infer<
  typeof createCoachMemoryItemSchema
>;

// Correcting an item. Deliberately NOT an update: the service supersedes the
// old row and writes a new one, so the previous wording survives. The wording
// and the area may be corrected; what KIND of claim it is and where it came
// from may not (TES-1019): an AI observation reworded by the coach is still an
// AI observation from the same source, and a doctor's handoff stays one. An
// older client's `factType` is ignored.
//
// TES-1192 — the class and a temporary context's dates, impact and agreement
// may be corrected too. What is omitted carries over from the current
// version; an empty impact or agreement clears it. The service re-checks the
// combined result with temporalFieldIssues. A new follow-up is scheduled with
// "Följ upp senare", not here.
export const reviseCoachMemoryItemSchema = z.object({
  body: z.string().trim().min(1).max(4000),
  category: coachMemoryCategorySchema.optional(),
  memoryClass: coachMemoryClassSchema.optional(),
  validFrom: isoDateSchema.optional(),
  validUntil: isoDateSchema.optional(),
  impact: z.string().trim().max(2000).optional(),
  agreement: z.string().trim().max(2000).optional(),
});
export type ReviseCoachMemoryItemRequest = z.infer<
  typeof reviseCoachMemoryItemSchema
>;

// Switching an item off without claiming it was wrong (`inactive`) or marking
// a question answered (`completed`). Superseding is not reachable here — only
// a revision produces it. TES-1019 (Emelie, point 12): removal is a traceable
// inactivation, so an older client's `deleted` is stored as `inactive`;
// `deleted` is left to the source-deletion cascade.
export const retireCoachMemoryItemSchema = z.object({
  lifecycle: z
    .enum(["inactive", "completed", "deleted"])
    .transform((l) => (l === "deleted" ? "inactive" : l)),
});
export type RetireCoachMemoryItemRequest = z.input<
  typeof retireCoachMemoryItemSchema
>;

export const coachFollowUpSchema = z.object({
  id: z.string(),
  patientId: z.string(),
  coachId: z.string(),
  dueAt: z.string(),
  prompt: z.string(),
  sourceType: coachMemorySourceTypeSchema,
  sourceId: z.string(),
  status: coachFollowUpStatusSchema,
  // The model may propose a follow-up; a proposal is not a follow-up until a
  // coach confirms it. Both flags are needed: `suggestedByAi` records where it
  // came from, `confirmedByEmployeeId` records that a human accepted it.
  suggestedByAi: z.boolean(),
  confirmedByEmployeeId: z.string().nullable(),
  confirmedAt: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  // TES-1400 (Ö-426) — who answers for it and where it comes back. A row
  // written before reads as its setter's, in "Uppföljningar".
  owner: z.lazy(() => followUpOwnerSchema),
  returnLane: workQueueLaneSchema,
});
export type CoachFollowUp = z.infer<typeof coachFollowUpSchema>;

// TES-1400 (Ö-426, Emelie 2026-10-15) — "Följ upp senare":
//   "Funktionen kräver alltid: ansvarig person eller roll, datum och tid för återkomst, vilken arbetskö ärendet återkommer i, synlig orsak, auditlogg. Ett ärende får inte försvinna till en ospecificerad senarelista."
// The responsible party is one person (an employee id) or a clinical role;
// the date and time is `dueAt`; the queue is one of the owning role's lanes;
// the visible reason is `prompt`. The server audits all four.
export const FOLLOW_UP_OWNER_ROLES = ["coach", "doctor"] as const;
export const followUpOwnerRoleSchema = z.enum(FOLLOW_UP_OWNER_ROLES);
export type FollowUpOwnerRole = z.infer<typeof followUpOwnerRoleSchema>;
export const followUpOwnerSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("person"), employeeId: z.string().min(1) }),
  z.object({ kind: z.literal("role"), role: followUpOwnerRoleSchema }),
]);
export type FollowUpOwner = z.infer<typeof followUpOwnerSchema>;

/** The lanes a follow-up owned by `role` may come back in. */
export function followUpLanesFor(
  role: FollowUpOwnerRole,
): readonly WorkQueueLane[] {
  return role === "coach" ? COACH_WORK_QUEUE_LANES : DOCTOR_WORK_QUEUE_LANES;
}

// Ours (listed in the PR): the role as the owner picker names it.
export const FOLLOW_UP_OWNER_ROLE_LABEL: Record<FollowUpOwnerRole, string> = {
  coach: "Coacherna",
  doctor: "Läkarna",
};

// A confirmed follow-up (one a person sets) names its owner and its lane;
// only a model's unconfirmed proposal may leave them out, and is given its
// setter and "Uppföljningar" until someone confirms it.
export const createCoachFollowUpSchema = z
  .object({
    dueAt: z.string().datetime(),
    prompt: z.string().trim().min(1).max(2000),
    sourceType: coachMemorySourceTypeSchema,
    sourceId: z.string().min(1),
    suggestedByAi: z.boolean().optional(),
    owner: followUpOwnerSchema.optional(),
    returnLane: workQueueLaneSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.suggestedByAi) return;
    if (!v.owner) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["owner"],
        message: "follow_up_owner_required",
      });
    }
    if (!v.returnLane) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["returnLane"],
        message: "follow_up_return_lane_required",
      });
    }
  });
export type CreateCoachFollowUpRequest = z.infer<
  typeof createCoachFollowUpSchema
>;

export const resolveCoachFollowUpSchema = z.object({
  status: z.enum(["completed", "cancelled"]),
});
export type ResolveCoachFollowUpRequest = z.infer<
  typeof resolveCoachFollowUpSchema
>;
